import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useRouter } from 'next/router'
import { OutlineMagicTextBoxEditorProps } from './OutlineMagicTextBoxEditor.types'
import { MagicTextBoxEditor } from '@/components/ContentTreeNode/ContentTreeLeafNode/MagicTextBoxEditor/MagicTextBoxEditor'
import { MagicTextBoxEditorTab } from '@/components/ContentTreeNode/ContentTreeLeafNode/MagicTextBoxEditor/MagicTextBoxEditor.types'
import { activeMTBEditorTabAtom } from '@/atoms/activeMTBEditorTab'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { authStateAtom, frontendDisplayedCourseSelector } from '@/atoms/auth'
import { UserData } from 'magic-text-box-lexical/src/atoms/annotations/annotations.types'
import {
    AnnotationSelection,
    CommentMetadata,
    UserType,
} from 'magic-text-box-lexical/src/context/AnnotatedContentContext/AnnotatedContent.types'
import { AnnotatedContentProps } from 'magic-text-box-lexical/src/components/MagicTextBoxCore/MagicTextBoxCore.types'
import { buildAnnotatedContentProvider } from '@/components/ContentTreeNode/ContentTreeLeafNode/MagicTextBoxEditor/buildAnnotatedContentProvider'
import { CopyCommentContextModal } from '@/components/ContentCommentThread/ReadComment/CopyCommentContextModal/CopyCommentContextModal'
import { ContentType } from 'common/src/commentThread/types'
import { hashContentPath } from 'common/src/ContentPath'
import { encodeOutlineCommentData } from 'common/src/commentThread/OutlineCommentData'
import { OutlineCommentReplyDraft } from './OutlineCommentReplyDraft'

export const OutlineMagicTextBoxEditor: React.FC<
    OutlineMagicTextBoxEditorProps
> = (props): ReactElement => {
    const { contentPath, type, ...restProps } = props
    const authState = useRecoilValue(authStateAtom)
    const userData = useMemo(
        (): UserData => ({
            userID: authState.contentCreatorID,
            userType: UserType.instructor,
            displayName: authState.displayName,
        }),
        [authState]
    )
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const annotatedContentProps = useMemo(
        (): AnnotatedContentProps => ({
            userData,
            annotatedContentProvider: buildAnnotatedContentProvider(
                courseName,
                contentPath,
                type
            ),
        }),
        [contentPath, courseName, type, userData]
    )

    // Copy comment context modal state
    const [copyContextModalData, setCopyContextModalData] = useState<{
        comment: CommentMetadata
        selection: AnnotationSelection
    } | null>(null)

    const handleCopyCommentContext = useCallback(
        (comment: CommentMetadata, selection: AnnotationSelection): void => {
            setCopyContextModalData({ comment, selection })
        },
        []
    )

    const handleCloseCopyContextModal = useCallback((): void => {
        setCopyContextModalData(null)
    }, [])

    const contentType =
        type === 'outline' ? ContentType.OUTLINE : ContentType.SUMMARY

    // Comments are viewed/answered in "Student View" mode. When arriving via a
    // comment link (showCommentThread), open the editor there instead of the
    // default Edit View.
    const router = useRouter()
    const setActiveMTBEditorTab = useSetRecoilState(activeMTBEditorTabAtom)
    useEffect(() => {
        if (router.query.showCommentThread) {
            setActiveMTBEditorTab(MagicTextBoxEditorTab.nonEditable)
        }
    }, [router.query.showCommentThread, setActiveMTBEditorTab])

    // Render an AI reply draft beneath each STUDENT comment in the annotation
    // panel. contentId must match the WB thread key (see buildAnnotatedContentProvider,
    // addComment) so fetch/trigger resolve to the right thread.
    const renderCommentReplyDraft = useCallback(
        (
            comment: CommentMetadata,
            _commentThreadID: string,
            selection: AnnotationSelection
        ): ReactElement | null => {
            if (comment.userType !== UserType.student) return null
            return (
                <OutlineCommentReplyDraft
                    commentId={comment.id}
                    courseName={courseName}
                    contentType={contentType}
                    contentId={encodeOutlineCommentData({
                        contentPath,
                        type,
                        annotationSelection: selection,
                    })}
                    contentPath={contentPath}
                    comment={{
                        displayName: comment.displayName,
                        isAnonymous: comment.isAnonymous,
                        createdAt: comment.createdAt,
                    }}
                />
            )
        },
        [courseName, contentType, contentPath, type]
    )

    return (
        <>
            <MagicTextBoxEditor
                {...restProps}
                annotatedContentProps={annotatedContentProps}
                cannotComment={props.type === 'summary'}
                canCopyAnnotationSelectionToClipboard={true}
                onCopyCommentContext={handleCopyCommentContext}
                renderCommentReplyDraft={renderCommentReplyDraft}
            />
            {copyContextModalData && (
                <CopyCommentContextModal
                    commentMetadata={copyContextModalData.comment}
                    contentType={contentType}
                    contentId={hashContentPath(contentPath)}
                    contentPath={contentPath}
                    courseName={courseName}
                    commentThreadSelection={copyContextModalData.selection}
                    isVisible={true}
                    onClose={handleCloseCopyContextModal}
                />
            )}
        </>
    )
}
